// Morse code mappings
export const MORSE_CODE = {
    'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.',
    'G': '--.', 'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..',
    'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.',
    'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
    'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---',
    '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...',
    '8': '---..', '9': '----.'
};

// Constants for timing calculations
const DOT_DURATION_MS_AT_1_WPM = 1200;
const DASH_MULTIPLIER = 3;

// All available characters
export const ALL_CHARACTERS = Object.keys(MORSE_CODE);
export const LETTERS = ALL_CHARACTERS.slice(0, 26);
export const NUMBERS = ALL_CHARACTERS.slice(26);

/**
 * Calculate dot duration in milliseconds for a given WPM
 * @param {number} wpm - Words per minute
 * @returns {number} Dot duration in milliseconds
 */
export function calculateDotDurationMs(wpm) {
    // Ensure WPM is within valid bounds
    if (wpm < 10) wpm = 10;
    if (wpm > 50) wpm = 50;
    return DOT_DURATION_MS_AT_1_WPM / wpm;
}

/**
 * Calculate dash duration in milliseconds for a given WPM
 * @param {number} wpm - Words per minute
 * @returns {number} Dash duration in milliseconds
 */
export function calculateDashDurationMs(wpm) {
    return calculateDotDurationMs(wpm) * DASH_MULTIPLIER;
}

/**
 * Calculate inter-symbol spacing in milliseconds for a given WPM
 * @param {number} wpm - Words per minute
 * @returns {number} Symbol spacing in milliseconds
 */
export function calculateSymbolSpacingMs(wpm) {
    return calculateDotDurationMs(wpm);
}

/**
 * Calculate inter-character spacing in milliseconds for a given WPM
 * @param {number} wpm - Words per minute
 * @returns {number} Character spacing in milliseconds
 */
export function calculateCharacterSpacingMs(wpm) {
    return calculateDotDurationMs(wpm) * 3;
}

/**
 * Get morse code pattern for a character
 * @param {string} char - Character to get morse code for
 * @returns {string|null} Morse code pattern or null if not found
 */
export function getMorseCode(char) {
    return MORSE_CODE[char.toUpperCase()] || null;
}

/**
 * Calculate total duration for playing a morse code pattern
 * @param {string} morseCode - Morse code pattern (e.g., '.-')
 * @param {number} wpm - Words per minute
 * @returns {number} Total duration in milliseconds
 */
export function calculateMorseDurationMs(morseCode, wpm) {
    if (!morseCode) return 0;

    const dotDurationMs = calculateDotDurationMs(wpm);
    const dashDurationMs = calculateDashDurationMs(wpm);
    const symbolSpacingMs = calculateSymbolSpacingMs(wpm);

    let totalDuration = 0;
    let validSymbolCount = 0;

    for (let i = 0; i < morseCode.length; i++) {
        const symbol = morseCode[i];
        if (symbol === '.') {
            totalDuration += dotDurationMs;
            validSymbolCount++;
        } else if (symbol === '-') {
            totalDuration += dashDurationMs;
            validSymbolCount++;
        }
        // Skip invalid characters
    }

    // Add inter-symbol spacing based on valid symbols
    if (validSymbolCount > 1) {
        totalDuration += symbolSpacingMs * (validSymbolCount - 1);
    }

    return totalDuration;
}

// Pattern groupings based on common confusion patterns
export const PATTERN_GROUPS = {
    'All Dots': {
        chars: ['E', 'I', 'S', 'H', '5'],
        description: 'Short patterns - hear the pure rhythm',
        difficulty: 'beginner'
    },
    'All Dashes': {
        chars: ['T', 'M', 'O', '0'],
        description: 'Long patterns - feel the flow',
        difficulty: 'beginner'
    },
    'Mixed Simple': {
        chars: ['A', 'N', 'U', 'D', 'R', 'W', 'K', 'G'],
        description: 'Basic mix of dots and dashes',
        difficulty: 'beginner'
    },
    'Mirror Reversals': {
        chars: ['F', 'L', 'G', 'W', 'Q', 'Y'],
        description: 'Exact opposite patterns',
        pairs: [['F', 'L'], ['G', 'W'], ['Q', 'Y']]
    },
    'Same Length': {
        chars: ['E', 'T', 'I', 'A', 'N', 'M', 'U', 'D'],
        description: 'Same duration, different rhythm',
        pairs: [['E', 'T'], ['I', 'A'], ['N', 'M'], ['U', 'D']]
    },
    'Pattern Prefix': {
        chars: ['J', '1', 'B', '6', 'D', 'B', 'P', 'X'],
        description: 'Same beginning, different ending',
        pairs: [['J', '1'], ['B', '6'], ['D', 'B'], ['P', 'X']]
    },
    'Sound-Alike': {
        chars: ['V', '4', 'S', 'H', 'Z', '7', '2', '3'],
        description: 'Similar at high speeds',
        pairs: [['V', '4'], ['S', 'H'], ['Z', '7'], ['2', '3']]
    },
    'Complex Patterns': {
        chars: ['C', 'X', 'Q', 'Y', '8', '9'],
        description: 'Long patterns requiring patience',
        difficulty: 'advanced'
    }
};

// Common morse code confusions based on sound patterns
// These are used as fallbacks when no actual confusion data exists
export const COMMON_CONFUSIONS = {
    // Mirror patterns (dots become dashes and vice versa)
    'F': { char: 'L', reason: 'Mirror sounds' },
    'L': { char: 'F', reason: 'Mirror sounds' },
    'G': { char: 'W', reason: 'Mirror sounds' },
    'W': { char: 'G', reason: 'Mirror sounds' },
    'Q': { char: 'Y', reason: 'Mirror sounds' },
    'Y': { char: 'Q', reason: 'Mirror sounds' },
    
    // Similar rhythms/cadence
    'E': { char: 'T', reason: 'Brief sounds' },
    'T': { char: 'E', reason: 'Brief sounds' },
    'I': { char: 'A', reason: 'Similar flow' },
    'A': { char: 'I', reason: 'Similar flow' },
    'N': { char: 'M', reason: 'Similar cadence' },
    'M': { char: 'N', reason: 'Similar cadence' },
    'U': { char: 'D', reason: 'Similar rhythm' },
    'D': { char: 'U', reason: 'Similar rhythm' },
    
    // Extended patterns
    'S': { char: 'H', reason: 'Extended pattern' },
    'H': { char: '5', reason: 'Extended pattern' },
    '5': { char: 'H', reason: 'Shortened pattern' },
    'B': { char: '6', reason: 'Similar sound' },
    '6': { char: 'B', reason: 'Similar sound' },
    'V': { char: '4', reason: 'Similar at speed' },
    '4': { char: 'V', reason: 'Similar at speed' },
    
    // Complex patterns
    'J': { char: '1', reason: 'Complex pattern' },
    '1': { char: 'J', reason: 'Complex pattern' },
    'O': { char: '0', reason: 'Flowing pattern' },
    '0': { char: 'O', reason: 'Flowing pattern' },
    'Z': { char: '7', reason: 'Mixed pattern' },
    '7': { char: 'Z', reason: 'Mixed pattern' },
    
    // Other confusions
    'C': { char: 'K', reason: 'Alternating sound' },
    'K': { char: 'C', reason: 'Alternating sound' },
    'R': { char: 'L', reason: 'Rolling sound' },
    'P': { char: 'X', reason: 'Complex rhythm' },
    'X': { char: 'P', reason: 'Complex rhythm' },
    '2': { char: '3', reason: 'Number pattern' },
    '3': { char: '2', reason: 'Number pattern' },
    '8': { char: '9', reason: 'Long pattern' },
    '9': { char: '8', reason: 'Long pattern' }
};

/**
 * Get all confusable pairs for a character
 * @param {string} char - Character to find confusable pairs for
 * @returns {string[]} Array of confusable characters
 */
export function getConfusableCharacters(char) {
    const confusable = [];

    Object.values(PATTERN_GROUPS).forEach(group => {
        if (group.pairs && group.chars.includes(char)) {
            group.pairs.forEach(pair => {
                if (pair.includes(char)) {
                    const other = pair.find(c => c !== char);
                    if (other && !confusable.includes(other)) {
                        confusable.push(other);
                    }
                }
            });
        }
    });

    return confusable;
}

/**
 * Calculate similarity between two morse code patterns
 * @param {string} code1 - First morse code pattern
 * @param {string} code2 - Second morse code pattern
 * @returns {number} Similarity score from 0 to 1
 */
export function calculateMorseSimilarity(code1, code2) {
    // Handle empty strings - both empty means identical
    if (!code1 && !code2) return 1;
    if (!code1 || !code2) return 0;

    const maxLen = Math.max(code1.length, code2.length);
    if (maxLen === 0) return 1;

    const distance = levenshteinDistance(code1, code2);
    return 1 - (distance / maxLen);
}

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance
 */
export function levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
            if (str2[i - 1] === str1[j - 1]) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    matrix[i][j - 1] + 1,     // insertion
                    matrix[i - 1][j] + 1      // deletion
                );
            }
        }
    }

    return matrix[str2.length][str1.length];
}